import {CommonModule} from '@angular/common';
import {Component, Input, OnDestroy} from '@angular/core';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faCheck, faCopy} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {copyTextToClipboard} from './clipboard.util';

@Component({
    selector: 'app-copy-button',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule],
    template: `
        <button
                type="button"
                class="copy-button"
                [class.copy-button--copied]="copied"
                [disabled]="!normalizedValue || disabled"
                [title]="copied ? 'Copied' : copyTitle"
                [attr.aria-label]="copied ? 'Copied' : copyTitle"
                (click)="copy($event)">
            <fa-icon [icon]="copied ? ['fas', 'check'] : ['fas', 'copy']"></fa-icon>
        </button>
    `,
    styles: [`
        .copy-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            border: 1px solid #d1d5db;
            border-radius: 0.375rem;
            background: #fff;
            color: #475467;
            cursor: pointer;
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }

        .copy-button:hover:not(:disabled) {
            background: #f9fafb;
            color: #111827;
        }

        .copy-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .copy-button--copied {
            color: #16A34A;
            border-color: #86EFAC;
            background: #F0FDF4;
        }
    `]
})
export class CopyButtonComponent implements OnDestroy {
    @Input() value: string | null | undefined = '';
    @Input() copyTitle = 'Copy to clipboard';
    @Input() disabled = false;
    @Input() resetMs = 2000;

    copied = false;
    private resetTimer: ReturnType<typeof setTimeout> | null = null;
    private copyInProgress = false;

    constructor(library: FaIconLibrary, private toastr: ToastrService) {
        library.addIcons(faCopy, faCheck);
    }

    get normalizedValue(): string {
        return (this.value || '').trim();
    }

    copy(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.copyInProgress) {
            return;
        }

        const value = this.normalizedValue;
        if (!value || value === '-') {
            return;
        }

        this.copyInProgress = true;
        void copyTextToClipboard(value).then((success) => {
            this.copyInProgress = false;
            if (!success) {
                this.toastr.error('Unable to copy value.', 'Copy failed');
                return;
            }
            this.copied = true;
            if (this.resetTimer) {
                clearTimeout(this.resetTimer);
            }
            this.resetTimer = setTimeout(() => {
                this.copied = false;
                this.resetTimer = null;
            }, this.resetMs);
        });
    }

    ngOnDestroy(): void {
        if (this.resetTimer) {
            clearTimeout(this.resetTimer);
        }
    }
}
